import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivate } from '@angular/router';
import { environment } from 'src/environments/environment';
import { logindetails } from '../../services/login-details';
import { isRoleAllowedForRoute } from './route-role-policy';
import { classifyRoleTier, resolveAffiliateCode } from '../../services/role-claims.util';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService implements CanActivate {

  role: any = '';
  affliates: any;
  companyAfl: any;
  // Set when app.component.ts detects the id token's identity doesn't match the signed-in
  // user. While true, this guard forces EVERY route except /unauthorized to bounce back to
  // /unauthorized - this is the single choke point all navigation passes through, so it
  // reliably blocks other, independent flows (e.g. header.component.ts's own role/affiliate
  // fetch) from navigating the user away before a full page reload clears the flag.
  private identityMismatched = false;

  // In-memory only (never localStorage) - deliberately wiped on every hard reload/tab close.
  // Callers must repopulate these via setTokens() after a fresh refresh-token exchange
  // (see getRefreshToken() below) rather than reading them off disk.
  private idToken: string | null = null;
  private accessToken: string | null = null;
  private newIdTokenKey: string | null = null;

  // True only for the brief window between app.component.ts detecting a fresh OAuth `code`
  // in the URL and that code being exchanged for tokens. Lets canActivate() quietly hold off
  // on the throwaway initial route navigation instead of bouncing it back to Azure login
  // while the real exchange (which will set accessToken) is still in flight.
  private loginExchangeInProgress = false;

  constructor(public router: Router) { }

  setLoginExchangeInProgress(value: boolean): void {
    this.loginExchangeInProgress = value;
  }
  getRole(): 'Admin' | 'Approver' | 'User' | 'ApproverAdmin'|'Unauthorized'{
    return this.role;
  }

  // Stores the 3 bearer tokens in memory and the refresh token in sessionStorage - the sole
  // exception, kept so a same-tab reload can silently re-derive the other 3 instead of forcing
  // a full login redirect. Tab-scoped on purpose: a new tab/browser restart no longer inherits
  // it, unlike localStorage. See the getRefreshToken()/clearTokens() docs below for why.
  setTokens(tokens: { idToken: string; accessToken: string; refreshToken: string; newIdTokenKey: string }): void {
    this.idToken = tokens.idToken;
    this.accessToken = tokens.accessToken;
    this.newIdTokenKey = tokens.newIdTokenKey;
    try {
      sessionStorage.setItem('Refresh_token', tokens.refreshToken);
    } catch (error) {
      console.error('[AuthGuardService] Unable to persist refresh token to sessionStorage:', error);
    }
  }

  getIdToken(): string | null {
    return this.idToken;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getNewIdTokenKey(): string | null {
    return this.newIdTokenKey;
  }

  // The one token still persisted (in sessionStorage, not localStorage) - see setTokens() for why.
  getRefreshToken(): string | null {
    try {
      return sessionStorage.getItem('Refresh_token');
    } catch (error) {
      console.error('[AuthGuardService] Unable to read refresh token from sessionStorage:', error);
      return null;
    }
  }

  // Call this alongside every existing localStorage.clear()/sessionStorage.clear()
  // logout/failure path so the in-memory tokens don't outlive the session they belong to.
  clearTokens(): void {
    this.idToken = null;
    this.accessToken = null;
    this.newIdTokenKey = null;
    try {
      sessionStorage.removeItem('Refresh_token');
    } catch (error) {
      console.error('[AuthGuardService] Unable to clear refresh token from sessionStorage:', error);
    }
  }

setRole(role: 'Admin' | 'Approver' | 'User' | 'ApproverAdmin'|'Unauthorized') {
  this.role = role;
}
getAffliate() {
  return this.affliates;
}

setAffliate(af: any) {
  this.affliates = af;
}

getCompanyAfl() {
  return this.companyAfl;
}

setCompanyAfl(cp: any) {
  this.companyAfl = cp;
}

setIdentityMismatched(value: boolean): void {
  this.identityMismatched = value;
}

isIdentityMismatched(): boolean {
  return this.identityMismatched;
}

canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {//NOSONAR
  if (this.identityMismatched && _route.routeConfig?.path !== 'unauthorized') {
    this.router.navigate(['/unauthorized']);
    return false;
  }
  const codefromurl = localStorage.getItem('codeResponse');
  // `codeResponse` only proves this BROWSER completed login at some point in the past - it
  // is set once in localStorage and never expires on its own. With the refresh token now
  // tab-scoped in sessionStorage (not shared across tabs like localStorage used to be), it is
  // NOT proof this TAB currently has a usable session: a brand-new tab, or a browser reopened
  // after all tabs were closed, still has the old codeResponse lying around but no way to
  // obtain real tokens. Require an actual in-memory access token (repopulated by the
  // APP_INITIALIZER from THIS tab's own refresh token, if any) before treating the tab as
  // authenticated - otherwise protected routes render and fire API calls with no token.
  if (codefromurl !== null && this.accessToken) {//NOSONAR
    // ------------------------------------------------------------------------------
    // Clear old storage
    if (localStorage.getItem('ADRUM_AGENT_INFO')!== null || localStorage.getItem('ADRUM_CLIENTINFO')!== null 
      || localStorage.getItem('accessToken')!== null || localStorage.getItem('firstTimeLogin')!== null || localStorage.getItem('currentUser')!== null) {
      sessionStorage.clear();
      localStorage.clear();
      // Storage was just wiped - the in-memory tokens must be reset too, or a delayed/blocked
      // navigation would leave this tab holding credentials no longer backed by any storage.
      this.clearTokens();
      // state.url is Angular's own resolved destination path (e.g. '/admin', or '/home' after
      // the '' -> 'home' redirect) - reliable regardless of whether the address bar has been
      // updated yet, unlike window.location.href (which only reflects an internal redirect
      // AFTER guards approve, per the router's deferred URL update strategy).
      localStorage.setItem('redirectUrl', state.url);
      const redirectUrl = environment.baseLoginUrl + logindetails.codeChallenge;
      window.location.href = redirectUrl;
      // Stale session detected — sending the user back to login, so this route must not activate.
      return false;
    }
    // -------------------------------------------------------------------------------
    // `codeResponse` is written to localStorage SYNCHRONOUSLY by app.component.ts's
    // NavigationStart handler, right before it `await`s the actual token exchange
    // (getTokens()). Angular evaluates this guard for the SAME initial navigation (the
    // '' -> 'home' redirect) essentially immediately after, so without this check it used
    // to return true well before AOV_IDTOKEN/id_token existed - letting the route's
    // components construct and fire every API call with no token at all (a burst of 401s),
    // which then made the auth interceptor's refresh flow fail too (no Refresh_token yet
    // either) and hard-redirect back to login, i.e. the "401s then auto-reload" symptom.
    // Once a fresh login is actually in flight (codeResponse set but the id token hasn't
    // been decoded/stored yet), block activation here. app.component.ts ALWAYS explicitly
    // navigates to the real destination (or /unauthorized, or back to login) once the
    // exchange finishes - wrapped in a try/catch/finally there specifically so that
    // follow-up navigation is guaranteed to fire exactly once no matter what happens
    // during the exchange (see app.component.ts NavigationStart handler). Do NOT also make
    // THIS guard wait/poll for AOV_IDTOKEN and resolve on its own - that was tried and
    // caused a SECOND, competing navigation to fire alongside app.component.ts's explicit
    // one (both racing to activate 'home'), which visibly looked like the app "loading
    // 2-3 times". A guard for a blocked route must stay a single, immediate, synchronous
    // decision - app.component.ts's own explicit navigate() is the only thing allowed to
    // retry it. Exempt the no-token, no-API-call landing pages (unauthorized/not-found/
    // wildcard) so app.component.ts can still navigate to THEM mid-exchange (e.g. a JWT
    // verification failure during login, which sets identityMismatched and navigates to
    // /unauthorized before any token is stored).
    const exemptPaths = ['unauthorized', 'not-found', '**'];
    // AOV_IDTOKEN now lives in sessionStorage (see app.component.ts/header.component.ts/
    // view-questions.component.ts - all migrated together as part of the sessionStorage
    // token-storage change), not localStorage - this must read the same place they write to.
    if (sessionStorage.getItem('AOV_IDTOKEN') === null && !exemptPaths.includes(_route.routeConfig?.path ?? '')) {
      return false;
    }
    // SECURITY (AppSec High Finding #9 - "No role-based route guard", round 2 closure,
    // 2026-09-29): previously /admin and /approvals were protected ONLY by a post-hoc
    // redirect in app.component.ts's NavigationStart handler + a duplicated copy in
    // header.component.ts, which both run AFTER this guard already let the navigation
    // through - the lazy route chunk had already loaded and the component had already
    // started firing API calls before the redirect ever fired. This check runs a
    // CENTRALIZED policy (route-role-policy.ts) here instead, BEFORE the route/chunk
    // activates, so an unauthorized-for-this-route session never reaches the component at
    // all. `resolveEffectiveRole()` closes the PRIOR fail-open window (this check used to be
    // skipped entirely whenever `this.role` was still '' right after a fresh login, before
    // onGetRole() resolved it) by synchronously deriving Admin/ApproverAdmin/User from the
    // id token claims already sitting in sessionStorage - falling back to the
    // least-privileged 'User' tier (not full access) for the narrow remaining case where
    // even that can't be determined yet (still needs an async approver lookup).
    const effectiveRole = this.resolveEffectiveRole();
    if (!isRoleAllowedForRoute(effectiveRole, _route.routeConfig?.path ?? '')) {
      this.router.navigate(['/unauthorized']);
      return false;
    }
    return true;
  }
  else if (this.loginExchangeInProgress) {
    // A fresh OAuth code is being exchanged for tokens right now (see app.component.ts) -
    // quietly hold off on this throwaway navigation instead of redirecting to login;
    // app.component.ts drives its own navigate() once the exchange finishes.
    return false;
  }
  else {
    localStorage.setItem('redirectUrl', state.url);
    const redirectUrl = environment.baseLoginUrl + logindetails.codeChallenge;
    window.location.href = redirectUrl;
    // Not authenticated — sending the user to login, so this route must not activate.
    return false;
  }
}

// SECURITY (AppSec High Finding #9 assurance work): resolves an authoritative role tier
// for THIS route-activation decision without waiting for header.component.ts's separate
// async getaffilate()/checkApproverId flow to complete. If `this.role` has already been
// explicitly set (setRole()), that authoritative value is always used. Otherwise, decodes
// the id token JSON that app.component.ts's login flow already wrote to sessionStorage
// (AOV_IDTOKEN) SYNCHRONOUSLY (no network call) and classifies Admin/ApproverAdmin/User
// purely from claims - the exact same rule AuthService.classifyRoleTier() uses elsewhere,
// via the shared, DI-free role-claims.util (kept separate from AuthService itself to avoid
// a circular DI dependency: AuthService already injects AuthGuardService). When no claim
// matches at all (a genuine Approver-vs-User split still needs the async is_approver
// lookup) or no id token is available yet, defaults to 'User' - the least-privileged
// AUTHENTICATED tier - rather than silently allowing full access during this transitional
// window.
private resolveEffectiveRole(): string {
  if (this.role) {
    return this.role;
  }
  try {
    const idTokenRaw = sessionStorage.getItem('AOV_IDTOKEN');
    if (!idTokenRaw) {
      return 'User';
    }
    const decoded = JSON.parse(idTokenRaw);
    const roles: string[] = Array.isArray(decoded?.roles) ? decoded.roles : [];
    const affiliate = localStorage.getItem('Affiliate') || resolveAffiliateCode(roles);
    return classifyRoleTier(roles, affiliate) ?? 'User';
  } catch (error) {
    console.error('[AuthGuardService] Could not synchronously resolve a role from the id token - defaulting to the least-privileged tier for this route check.', error);
    return 'User';
  }
}
}
